type Obj = {
  "FUEL TYPE": string
  MANUFACTURER: string
  MODEL: string
  STATE: string
  "VEHICLE TYPE": string
  [key: string]: any
}

type SortConfig = {
  key: string // Allows sorting by "All fuels" as well
  direction: "ascending" | "descending"
}

const getMostRecentQuarter = (obj: Obj) => {
  const quarterKeys = Object.keys(obj).filter((key) => /^Q\d \d{4}$/.test(key))

  // Sort the keys to find the most recent quarter
  const sortedQuarterKeys = quarterKeys.sort((a, b) => {
    const [quarterA, yearA] = a.split(" ")
    const [quarterB, yearB] = b.split(" ")

    // Compare year first
    if (yearA !== yearB) {
      return parseInt(yearA) - parseInt(yearB)
    }

    // Compare quarter if years are the same
    return parseInt(quarterA[1]) - parseInt(quarterB[1])
  })

  // Return the last key (most recent quarter)
  return sortedQuarterKeys.pop()
}

const groupedByVehicleType = (data: Obj[]): Record<string, Obj[]> => {
  return data.reduce((acc: Record<string, Obj[]>, obj) => {
    const vehicleType = obj["VEHICLE TYPE"] ?? obj["MARKET SEGMENT"]
    if (!acc[vehicleType]) {
      acc[vehicleType] = []
    }

    const mostRecentQuarter = getMostRecentQuarter(obj)

    // Include only the most recent quarter data
    const relevantData = {
      ...obj,
      latestData: mostRecentQuarter ? obj[mostRecentQuarter] : null,
    }

    acc[vehicleType].push(relevantData)

    return acc
  }, {})
}

const groupedByFuelType = (data: Obj[]): Record<string, Obj[]> => {
  return data.reduce((acc: Record<string, Obj[]>, obj) => {
    const fuelType = obj["FUEL TYPE"]
    if (!acc[fuelType]) {
      acc[fuelType] = []
    }

    const mostRecentQuarter = getMostRecentQuarter(obj)

    // Include only the most recent quarter data
    const relevantData = {
      ...obj,
      latestData: mostRecentQuarter ? obj[mostRecentQuarter] : null,
    }

    acc[fuelType].push(relevantData)

    return acc
  }, {})
}

const groupedByManufacturerType = (data: Obj[]): Record<string, Obj[]> => {
  return data.reduce((acc: Record<string, Obj[]>, obj) => {
    const manufacturer = obj["MANUFACTURER"]
    if (!acc[manufacturer]) {
      acc[manufacturer] = []
    }

    const mostRecentQuarter = getMostRecentQuarter(obj)

    // Include only the most recent quarter data
    const relevantData = {
      ...obj,
      latestData: mostRecentQuarter ? obj[mostRecentQuarter] : null,
    }

    acc[manufacturer].push(relevantData)

    return acc
  }, {})
}

export const groupedByType = (evData: any[]) => {
  const fullData = evData.map((dataset) => JSON.parse(dataset?.data ?? ""))
  const grouped = groupedByVehicleType(fullData)

  const groupedArray = Object.keys(grouped).map((vehicleType) => ({
    "VEHICLE TYPE": vehicleType,
    vehicles: grouped[vehicleType],
  }))

  return groupedArray
}

export const groupedByFuel = (evData: any[]) => {
  const fullData = evData.map((dataset) => JSON.parse(dataset?.data ?? ""))
  const grouped = groupedByFuelType(fullData)

  const groupedArray = Object.keys(grouped).map((fuelType) => ({
    "FUEL TYPE": fuelType,
    vehicles: grouped[fuelType],
  }))

  if (!groupedArray.some((item) => item["FUEL TYPE"] === "ICE")) {
    groupedArray.push({
      "FUEL TYPE": "ICE",
      vehicles: [],
    })
  }

  if (!groupedArray.some((item) => item["FUEL TYPE"] === "Hybrid")) {
    groupedArray.push({
      "FUEL TYPE": "Hybrid",
      vehicles: [],
    })
  }

  if (!groupedArray.some((item) => item["FUEL TYPE"] === "BEV")) {
    groupedArray.push({
      "FUEL TYPE": "BEV",
      vehicles: [],
    })
  }

  if (!groupedArray.some((item) => item["FUEL TYPE"] === "PHEV")) {
    groupedArray.push({
      "FUEL TYPE": "PHEV",
      vehicles: [],
    })
  }

  if (!groupedArray.some((item) => item["FUEL TYPE"] === "HFCEV")) {
    groupedArray.push({
      "FUEL TYPE": "HFCEV",
      vehicles: [],
    })
  }
  return groupedArray
}

export const groupedByManufacturer = (evData: any[]) => {
  const fullData = evData.map((dataset) => JSON.parse(dataset?.data ?? ""))
  const grouped = groupedByManufacturerType(fullData)

  const groupedArray = Object.keys(grouped).map((manufactuerer) => ({
    MANUFACTURER: manufactuerer,
    vehicles: grouped[manufactuerer],
  }))

  return groupedArray
}

export const groupedByFuelAndJurisdiction = (evData: any[]) => {
  const fullData = evData.map((dataset) => JSON.parse(dataset?.data ?? ""))
  const grouped = groupedByFuelType(fullData)

  const groupedArray = Object.keys(grouped).map((fuelType) => ({
    "FUEL TYPE": fuelType,
    vehicles: {
      NSW: grouped[fuelType].filter((vehicle) => vehicle.STATE === "NEW SOUTH WALES"),
      VIC: grouped[fuelType].filter((vehicle) => vehicle.STATE === "VICTORIA"),
      QLD: grouped[fuelType].filter((vehicle) => vehicle.STATE === "QUEENSLAND"),
      SA: grouped[fuelType].filter((vehicle) => vehicle.STATE === "SOUTH AUSTRALIA"),
      WA: grouped[fuelType].filter((vehicle) => vehicle.STATE === "WESTERN AUSTRALIA"),
      TAS: grouped[fuelType].filter((vehicle) => vehicle.STATE === "TASMANIA"),
      NT: grouped[fuelType].filter((vehicle) => vehicle.STATE === "NORTHERN TERRITORY"),
      ACT: grouped[fuelType].filter((vehicle) => vehicle.STATE === "AUSTRALIAN CAPITAL TERRITORY"),
    },
  }))

  return groupedArray
}

export const colors = {
  All_fuels: "rgb(255, 255, 255)",
  ICE: "rgb(142, 142, 142)",
  BEV: "rgb(96, 124, 222)",
  PHEV: "rgb(255, 147, 68)",
  Hybrid: "rgb(255, 237, 72)",
  HFCEV: "rgb(151, 229, 245)",
  All_fuels_50: "rgba(255, 255, 255, 0.5)",
  ICE_50: "rgba(142, 142, 142, 0.5)",
  BEV_50: "rgba(96, 124, 222, 0.5)",
  PHEV_50: "rgba(255, 147, 68, 0.5)",
  Hybrid_50: "rgba(255, 237, 72, 0.5)",
  HFCEV_50: "rgba(151, 229, 245, 0.5)",
}

export const extractQuarterlyData = (obj: Obj): { quarter: string; value: number }[] => {
  // Filter the keys to only include those that match the quarterly pattern (e.g., "Q1 2022")
  const quarterKeys = Object.keys(obj).filter((key) => /^Q\d \d{4}$/.test(key))

  // Sort the keys to ensure they are in chronological order
  const sortedQuarterKeys = quarterKeys.sort((a, b) => {
    const [quarterA, yearA] = a.split(" ")
    const [quarterB, yearB] = b.split(" ")

    // Compare year first
    if (yearA !== yearB) {
      return parseInt(yearA) - parseInt(yearB)
    }

    // Compare quarter if years are the same
    return parseInt(quarterA[1]) - parseInt(quarterB[1])
  })

  // Map over the sorted keys to extract key-value pairs
  return sortedQuarterKeys.map((key) => ({ quarter: key, value: obj[key] }))
}

export const regions = [
  "NEW SOUTH WALES",
  "VICTORIA",
  "QUEENSLAND",
  "SOUTH AUSTRALIA",
  "WESTERN AUSTRALIA",
  "TASMANIA",
  "NORTHERN TERRITORY",
  "AUSTRALIAN CAPITAL TERRITORY",
]

export const fuelTypes = ["ICE", "BEV", "Hybrid", "PHEV", "HFCEV"]
